/**
 * Simular venda (plano 4.4; não grava nada): "se eu vender N ambientes para
 * entregar em tal data, cabe?".
 *
 * Duas perguntas independentes, e vale a PIOR resposta:
 * 1. Capacidade: a venda entra na fila pela data desejada e é reprojetada no
 *    mesmo ritmo e com a mesma faixa de precisão da página. Cabe quando até o
 *    pior caso da faixa termina a tempo (G dias úteis antes da entrega) sem
 *    empurrar cliente que hoje está no prazo.
 * 2. Fluxo: da venda ao carregamento a fábrica leva p25/mediana/p75 dias
 *    úteis (histórico). Pedir menos que o p25 é "prazo improvável,
 *    independente da capacidade" (decisão 12).
 *
 * Sem ritmo medido não há veredito: `{indisponivel:true, motivo}`. Número
 * inventado aqui viraria prazo prometido ao cliente.
 */
import { ehIsoValido, somarDu } from "./calendario";
import { CAMADA } from "./classificacao";
import { clientesEmpurrados, ocupacaoAntesDepois, periodosAte } from "./comparacao";
import { CLASSE, CLASSES } from "./excecoes";
import { cargaDoItem, diaDaPosicao, projetarFila } from "./fila";
import { ESCALA } from "./periodos";
import { anosSemCalendario } from "./ritmo";

export const MAX_AMBIENTES_SIMULADOS = 60;

export const VEREDITO = Object.freeze({ CABE: "cabe", FALAR_COM_PCP: "falar_com_pcp", NAO_CABE: "nao_cabe" });

// Do melhor para o pior: o veredito final é o maior índice.
const GRAVIDADE = [VEREDITO.CABE, VEREDITO.FALAR_COM_PCP, VEREDITO.NAO_CABE];

export const MOTIVO_SIMULACAO = Object.freeze({
  // capacidade
  TERMINA_COM_FOLGA: "termina_com_folga",
  TERMINA_NO_LIMITE: "termina_no_limite",
  TERMINA_DEPOIS: "termina_depois",
  EMPURRA_CLIENTES: "empurra_clientes",
  // fluxo
  PRAZO_SEGURO: "prazo_seguro",
  PRAZO_APERTADO: "prazo_apertado",
  PRAZO_IMPROVAVEL: "prazo_improvavel",
  FLUXO_SEM_DADOS: "fluxo_sem_dados",
});

export const INDISPONIVEL = Object.freeze({
  SEM_RITMO: "sem_ritmo",
  CALENDARIO_AUSENTE: "calendario_ausente",
});

export function piorVeredito(...vereditos) {
  return vereditos
    .filter(Boolean)
    .reduce((pior, v) => (GRAVIDADE.indexOf(v) > GRAVIDADE.indexOf(pior) ? v : pior), VEREDITO.CABE);
}

function validarEntrada(ambientes, entregaDesejada, hoje) {
  if (!Array.isArray(ambientes) || ambientes.length < 1 || ambientes.length > MAX_AMBIENTES_SIMULADOS) {
    throw new TypeError(`A simulação aceita de 1 a ${MAX_AMBIENTES_SIMULADOS} ambientes.`);
  }
  const invalida = ambientes.find((a) => !CLASSES.includes(a?.classe));
  if (invalida !== undefined) throw new TypeError(`Classe de ambiente inválida: ${String(invalida?.classe)}.`);
  if (!ehIsoValido(entregaDesejada) || entregaDesejada < hoje) {
    throw new TypeError(`A entrega desejada precisa ser uma data a partir de hoje (recebido: ${String(entregaDesejada)}).`);
  }
}

/**
 * Ambientes fictícios da venda. A classe (leve/normal/pesado) vai junto:
 * na contagem cada um vale 1; ela pesa na leitura com as exceções marcadas
 * (`excecoes.pesoDoItem` lê `item.classe` antes da marca do PCP).
 */
export function ambientesSimulados(ambientes, entregaDesejada) {
  return ambientes.map((ambiente, i) => ({
    identidade: `venda-simulada:${i + 1}`,
    chave: null,
    pedido: null,
    cliente: "Venda simulada",
    ambiente: `Ambiente ${i + 1}`,
    situacao: "aberto",
    camada: CAMADA.VENDA_SIMULADA,
    entrega: entregaDesejada,
    classe: ambiente.classe,
    sinais: [],
  }));
}

function vereditoDeCapacidade(termino, alvo, empurradosPiorCaso) {
  if (termino.min > alvo) return { veredito: VEREDITO.NAO_CABE, motivo: MOTIVO_SIMULACAO.TERMINA_DEPOIS };
  if (termino.max > alvo) return { veredito: VEREDITO.FALAR_COM_PCP, motivo: MOTIVO_SIMULACAO.TERMINA_NO_LIMITE };
  if (empurradosPiorCaso.length > 0) return { veredito: VEREDITO.FALAR_COM_PCP, motivo: MOTIVO_SIMULACAO.EMPURRA_CLIENTES };
  return { veredito: VEREDITO.CABE, motivo: MOTIVO_SIMULACAO.TERMINA_COM_FOLGA };
}

function vereditoDeFluxo(fluxo, entregaDesejada) {
  if (!fluxo?.suficiente) return { veredito: null, motivo: MOTIVO_SIMULACAO.FLUXO_SEM_DADOS };
  const { p25, p75 } = fluxo.seHojeAssinar;
  if (entregaDesejada >= p75) return { veredito: VEREDITO.CABE, motivo: MOTIVO_SIMULACAO.PRAZO_SEGURO };
  if (entregaDesejada >= p25) return { veredito: VEREDITO.FALAR_COM_PCP, motivo: MOTIVO_SIMULACAO.PRAZO_APERTADO };
  return { veredito: VEREDITO.NAO_CABE, motivo: MOTIVO_SIMULACAO.PRAZO_IMPROVAVEL };
}

function terminoDoUltimoSimulado(projecao, precisao) {
  const simulados = projecao.itens.filter((item) => item.camada === CAMADA.VENDA_SIMULADA);
  const ultimo = simulados[simulados.length - 1];
  const fatores = [1 + precisao.erroMin, 1 + precisao.erroMax];
  const dia = (fator) =>
    diaDaPosicao({
      posicao: cargaDoItem(ultimo),
      ritmo: projecao.ritmo,
      fator,
      hoje: projecao.hoje,
      naoUteis: projecao.naoUteis,
    });
  return {
    posicoes: { primeira: simulados[0].posicao, ultima: ultimo.posicao },
    termino: { min: dia(Math.min(...fatores)), central: ultimo.diaProjetado, max: dia(Math.max(...fatores)) },
  };
}

/**
 * A venda inserida numa projeção (`antes`), na mesma régua dela: mesmo ritmo
 * e mesmo peso por item. É a parte "capacidade" do veredito; a simulação
 * roda uma vez na régua oficial e, enquanto os pesos não são a oficial, outra
 * na leitura com as exceções marcadas.
 */
function capacidadeNaProjecao({ antes, abertos, ambientes, entregaDesejada, G, precisao }) {
  const { hoje, naoUteis } = antes;
  const projecao = projetarFila({
    abertos: [...(abertos || []), ...ambientesSimulados(ambientes, entregaDesejada)],
    ritmo: antes.ritmo,
    naoUteis,
    hoje,
    precisao,
    peso: antes.peso,
  });
  const { termino, posicoes } = terminoDoUltimoSimulado(projecao, precisao);
  const alvo = somarDu(entregaDesejada, -G, naoUteis);
  const empurrados = clientesEmpurrados({ antes, depois: projecao, G });
  const empurradosPiorCaso = clientesEmpurrados({
    antes,
    depois: projecao,
    G,
    fator: 1 + Math.max(precisao.erroMin, precisao.erroMax),
  });
  const veredito = vereditoDeCapacidade(termino, alvo, empurradosPiorCaso);
  return { projecao, termino, posicoes, alvo, empurrados, empurradosPiorCaso, ...veredito };
}

/**
 * A mesma venda na leitura com as exceções marcadas, enquanto ela ainda não
 * é a régua oficial (`painel.segundaLeitura`): é ali que leve e pesado pesam.
 * `null` quando não há segunda leitura a mostrar.
 */
function naSegundaLeitura({ painel, ambientes, entregaDesejada, G, precisao }) {
  const segunda = painel?.segundaLeitura;
  if (!segunda?.disponivel || segunda.oficial || !segunda.projecao?.ritmo) return null;
  // Sem marca nenhuma e só ambientes normais, a conta com os pesos é a própria
  // contagem: repetir o mesmo número como "segunda leitura" só confunde.
  const temMarca = Boolean(segunda.cobertura?.marcados || segunda.marcadosNaFila);
  if (!temMarca && ambientes.every((a) => (a?.classe ?? CLASSE.NORMAL) === CLASSE.NORMAL)) return null;
  const resultado = capacidadeNaProjecao({
    antes: segunda.projecao,
    abertos: painel.abertos,
    ambientes,
    entregaDesejada,
    G,
    precisao,
  });
  return {
    veredito: resultado.veredito,
    motivo: resultado.motivo,
    termino: resultado.termino,
    alvo: resultado.alvo,
    empurradosPiorCaso: resultado.empurradosPiorCaso.length,
    cobertura: segunda.cobertura,
  };
}

/**
 * `simularVenda({painel, ambientes, entregaDesejada, fluxo, G, anosComRegistro?, escala?})`
 * - `painel`: retorno de `calcularPainelCapacidade` (abertos, fila, ritmo,
 *   precisao, segundaLeitura);
 * - `ambientes`: `[{classe}]`, 1 a 60; `entregaDesejada`: ISO ≥ hoje;
 * - `fluxo`: `prazoDeFluxo`; `G`: `folgaTerminoCarregamento().G`.
 *
 * A venda é projetada na régua oficial do painel (`painel.fila`: a contagem,
 * ou os pesos quando as marcas já cobrem o histórico). Enquanto os pesos são
 * só a segunda leitura, `segundaLeitura` traz a mesma conta com a classe de
 * cada ambiente simulado pesando (informativa: o veredito é o da régua oficial).
 *
 * Retorna `{indisponivel:true, motivo, anos?}` ou
 * `{indisponivel:false, veredito, motivos:[{codigo, veredito}], capacidade:{veredito,
 * termino:{min, central, max}, alvo, G, posicoes}, fluxo:{veredito, ...fluxo},
 * empurrados, empurradosPiorCaso, ocupacao:[{periodo, antes, depois}], projecao,
 * segundaLeitura: null | {veredito, motivo, termino, alvo, empurradosPiorCaso, cobertura}}`.
 */
export function simularVenda({
  painel,
  ambientes,
  entregaDesejada,
  fluxo,
  G = 0,
  anosComRegistro,
  escala = ESCALA.MES,
} = {}) {
  const projecaoAntes = painel?.fila;
  if (!painel?.ritmo?.ritmo || !projecaoAntes?.ritmo) {
    return { indisponivel: true, motivo: painel?.ritmo?.motivo || INDISPONIVEL.SEM_RITMO };
  }
  const { hoje } = projecaoAntes;
  validarEntrada(ambientes, entregaDesejada, hoje);
  if (anosComRegistro) {
    const anos = anosSemCalendario(hoje, entregaDesejada, anosComRegistro);
    if (anos.length > 0) return { indisponivel: true, motivo: INDISPONIVEL.CALENDARIO_AUSENTE, anos };
  }

  const precisao = painel.precisao || projecaoAntes.precisao;
  const capacidade = capacidadeNaProjecao({
    antes: projecaoAntes,
    abertos: painel.abertos,
    ambientes,
    entregaDesejada,
    G,
    precisao,
  });
  const { projecao, termino, posicoes, alvo, empurrados, empurradosPiorCaso } = capacidade;
  const doFluxo = vereditoDeFluxo(fluxo, entregaDesejada);
  const motivos = [
    { codigo: capacidade.motivo, veredito: capacidade.veredito },
    { codigo: doFluxo.motivo, veredito: doFluxo.veredito },
  ];
  if (capacidade.motivo !== MOTIVO_SIMULACAO.EMPURRA_CLIENTES && empurradosPiorCaso.length > 0) {
    motivos.push({ codigo: MOTIVO_SIMULACAO.EMPURRA_CLIENTES, veredito: VEREDITO.FALAR_COM_PCP });
  }

  return {
    indisponivel: false,
    veredito: piorVeredito(capacidade.veredito, doFluxo.veredito),
    motivos,
    capacidade: { veredito: capacidade.veredito, termino, alvo, G, posicoes },
    fluxo: { ...(fluxo || { suficiente: false, n: 0 }), veredito: doFluxo.veredito },
    empurrados,
    empurradosPiorCaso,
    ocupacao: ocupacaoAntesDepois({
      antes: projecaoAntes,
      depois: projecao,
      periodos: periodosAte(escala, hoje, termino.max > entregaDesejada ? termino.max : entregaDesejada),
    }),
    projecao,
    segundaLeitura: naSegundaLeitura({ painel, ambientes, entregaDesejada, G, precisao }),
  };
}

// ------------------------------------------------------------------ link

const VERSAO_LINK = 1;

function paraBase64Url(texto) {
  return btoa(texto).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function deBase64Url(texto) {
  const base = texto.replace(/-/g, "+").replace(/_/g, "/");
  return atob(base + "=".repeat((4 - (base.length % 4)) % 4));
}

/**
 * Parâmetro `?simular=` do link compartilhável: base64url de
 * `{"v":1,"a":["normal",...],"e":"AAAA-MM-DD"}`. Sem nome de cliente e sem
 * valor de propósito: o link circula por WhatsApp.
 */
export function codificarSimulacao({ ambientes, entregaDesejada }) {
  return paraBase64Url(JSON.stringify({ v: VERSAO_LINK, a: ambientes.map((a) => a.classe), e: entregaDesejada }));
}

/**
 * `{ambientes:[{classe}], entregaDesejada}` ou `null` para qualquer coisa
 * que não seja um link válido desta versão (o link pode ter sido cortado
 * no caminho; abrir a simulação errada é pior do que não abrir).
 */
export function decodificarSimulacao(parametro) {
  if (typeof parametro !== "string" || !parametro) return null;
  try {
    const dados = JSON.parse(deBase64Url(parametro));
    if (dados?.v !== VERSAO_LINK || !Array.isArray(dados.a) || !ehIsoValido(dados.e)) return null;
    if (dados.a.length < 1 || dados.a.length > MAX_AMBIENTES_SIMULADOS) return null;
    if (!dados.a.every((classe) => CLASSES.includes(classe))) return null;
    return { ambientes: dados.a.map((classe) => ({ classe })), entregaDesejada: dados.e };
  } catch {
    // base64 ou JSON quebrado: link inválido, a página abre sem simulação.
    return null;
  }
}
